import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Stack, Tooltip, IconButton, Button, useMediaQuery, Grid, Box, Menu, MenuItem, ListItemIcon } from '@mui/material';
import { Edit, Delete, Photo, MoreVert } from '@mui/icons-material';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { Loading } from '../../../components/loading/Loading';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import EditProductModal from './modals/EditProductModal';
import { Modules } from '../../../utils/permissions/Modules';
import ImageUploadModal from '../../../components/image_upload_modal/ImageUploadModal';
import { useUserContext } from '../../../context/user_context/UserContext';
import { ImageUploadType } from '../../../components/image_upload_modal/ImageUploadType';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { productSnapshotListener } from '../../../repository/ProductsDAO';
import { CloudFunctions } from '../../../utils/enums/CloudFunctions';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

function ProductDetail() {
    const { productId } = useParams(); 
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [showAddImageModal, setShowAddImageModal] = useState(false);
    const { firebaseUser } = useUserContext();
    const { hasPermission } = usePermissions();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const { organizationStripeInfo } = useOrganizationContext();
    const { showToast } = useToast();
    const [anchorEl, setAnchorEl] = useState(null);

    //TOOD: COULDNT DELETE PRODUCT FROM STRIPE

    //ProductDetail.js:63 Uncaught (in promise) FirebaseError: Unable to delete product on Stripe.

    
    useEffect(() => {
        if (firebaseUser && productId) {
            const unsubscribeProduct = productSnapshotListener(firebaseUser.organization, productId, (productData) => {
                setProduct(productData);
                setLoading(false);
            });

            return () => unsubscribeProduct();
        }
    }, [productId, firebaseUser]);

    const handleDeleteProduct = async () => {
        setConfirmOpen(false);
        const functions = getFunctions();
        const deleteProduct = httpsCallable(functions, CloudFunctions.DeleteStripeProduct);
        const result = await deleteProduct({ productId: productId, accountId: organizationStripeInfo.stripe_account_id });

        if (result.data.success) {
            showToast("Success delete", ToastType.SUCCESS);
            navigate('/products');
        } else {
            console.error('Error deleting product:', result.data.error);
            showToast("Failed delete");
        }
    };

    if (loading) return <AppSidebar content={<Loading />} />;

    return (
        <AppSidebar
            content={
                <>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            {product.image_url ? (
                                <img 
                                    src={product.image_url} 
                                    alt={"Product Image"} 
                                    style={{ width: isMobile ? '70px' : '100px', height: isMobile ? '70px' : '100px', cursor: "pointer" }} 
                                    onClick={() => setShowAddImageModal(true)}
                                />
                            ) : (
                                <IconButton onClick={() => hasPermission(Modules.PRODUCTS, PermissionTypes.WRITE) && setShowAddImageModal(true)}>
                                    <Photo fontSize="large" />
                                </IconButton>
                            )}
                        </Grid>
                        {!isMobile && (
                            <Grid item>
                                {hasPermission(Modules.PRODUCTS, PermissionTypes.WRITE) && (
                                    <>
                                        <Button variant="text" startIcon={<Edit />} onClick={() => setShowEditProductModal(true)}>
                                            {t(StringKeys.EDIT)}
                                        </Button>
                                        <Button variant="text" startIcon={<Delete />} onClick={() => setConfirmOpen(true)}>
                                                {t(StringKeys.DELETE)}
                                        </Button>
                                    </>                                    
                                )}

                            </Grid>
                        )}
                        {isMobile && (
                            <Grid item>
                                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                    <MoreVert />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => handleMenuClose(setAnchorEl)}
                                >
                                    {hasPermission(Modules.PRODUCTS, PermissionTypes.WRITE) && (
                                        <MenuItem onClick={() => { setShowEditProductModal(true); handleMenuClose(setAnchorEl); }}>
                                            <ListItemIcon>
                                                <Edit fontSize="small" />
                                            </ListItemIcon>
                                            {t(StringKeys.EDIT)}
                                        </MenuItem>
                                    )}
                                    {hasPermission(Modules.PRODUCTS, PermissionTypes.DELETE) && (
                                        <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(setAnchorEl); }}>
                                            <ListItemIcon>
                                                <Delete fontSize="small" />
                                            </ListItemIcon>
                                            {t(StringKeys.DELETE)}
                                        </MenuItem>
                                    )}
                                </Menu>
                            </Grid>
                        )}
                    </Grid>

                    <Card variant="outlined" sx={{ mt: 3 }}>
                        <CardContent>
                        <Typography variant={isMobile ? "h6" : "h5"}>{product.name}</Typography>
                            <Typography>{t(StringKeys.DESCRIPTION)}: {product.description}</Typography>
                            <Typography>{t(StringKeys.PRICE)}: ${(product.default_price.unit_amount / 100).toFixed(2)} {product.default_price.currency && product.default_price.currency.toUpperCase()}</Typography>
                            <Typography>{t(StringKeys.CREATED_DATE)}: {new Date(product.created_at.seconds * 1000).toLocaleDateString()}</Typography>
                            
                            {/* Tax Information */}
                            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>{t(StringKeys.TAX_SETTINGS)}</Typography>
                            <Typography>
                                {t(StringKeys.INCLUDE_TAX)}: {' '}
                                {product.tax_behavior === 'inclusive' 
                                    ? t(StringKeys.YES) + ' (' + t(StringKeys.TAX_INCLUDED) + ')'
                                    : t(StringKeys.NO) + ' (' + t(StringKeys.TAX_ADDED) + ')'
                                }
                            </Typography>
                            <Typography>
                                {t(StringKeys.TAX_CODE)}: {' '}
                                {getTaxCodeName(product.tax_code || 'txcd_99999999')}
                            </Typography>

                            {/* <Typography>{t(StringKeys.POINT_OF_SALE_PRODUCT)}: {product.pos_item ? t(StringKeys.YES) : t(StringKeys.NO)}</Typography>
                            <Typography>{t(StringKeys.INVOICE_PRODUCT)}: {product.invoice_item ? t(StringKeys.YES) : t(StringKeys.NO)}</Typography> */}
                        </CardContent>
                    </Card>

                    <ConfirmDeleteModal
                        open={confirmOpen}
                        onClose={() => setConfirmOpen(false)}
                        onConfirm={handleDeleteProduct}
                        item={product.name} 
                    />
                    <EditProductModal 
                        product={product}
                        show={showEditProductModal}
                        onHide={() => setShowEditProductModal(false)}
                    />
                    <ImageUploadModal
                        show={showAddImageModal}
                        onHide={() => setShowAddImageModal(false)}
                        organization={firebaseUser.organization}
                        modalTitle={t(StringKeys.ADD_NEW_IMAGE)}
                        itemId={product.id}
                        cropShape="rect"
                        uploadType={ImageUploadType.ProductImage}                       
                    />
                </>
            }
        />
    );
}

function getTaxCodeName(code) {
    const taxCodes = {
        'txcd_99999999': 'General - Default',
        'txcd_20030000': 'Software as a service (SaaS)',
        'txcd_30070000': 'Professional services',
        'txcd_35060000': 'Digital goods',
        'txcd_10000000': 'Physical goods',
    };
    
    return taxCodes[code] || code;
}

export default ProductDetail;
